import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaPaperPlane,
  FaPaperclip,
  FaSmile,
  FaEllipsisV,
} from "react-icons/fa";
import "../../../assets/css/seller/message/SellerMessages.css";
import SellerLayout from "../../../layouts/SellerLayout";
import axios from "axios";
import Swal from "sweetalert2";
import MyContext from "../../../context/MyContext";
import { MessagesPageSkeleton } from "../../../components/general/skeleton/SkeletonBlock";
import { getEcho } from "../../../components/general/echo/Echo";

const SellerMessages = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, authToken } = useContext(MyContext);

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [loading, setLoading] = useState(true);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // viewAllConversations — already token-based tha, unchanged
  const viewAllConversations = () => {
    axios
      .get(`${baseUrl}/seller/conversations/viewAllConversations`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setConversations(response.data.conversations || []);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!authToken) {
      setLoading(false);
      navigate("/seller/login");
      return;
    }
    viewAllConversations();
  }, [authToken]);

  // ── real-time — jo conversation khuli hai uske channel pe naya
  // message aate hi list mein append kar do, poori history dobara
  // fetch karne ki zaroorat nahi ──
  useEffect(() => {
    if (!authToken || !selectedChat) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channelName = `conversation.${selectedChat.id}`;
    const channel = echo.private(channelName);

    channel.listen('.new-message', (e) => {
      const incoming = e.message;
      setMessages((prev) => {
        // ── dedup — apna hi bheja hua message dobara na add ho ──
        if (prev.some((m) => m.id === incoming.id)) return prev;
        return [...prev, incoming];
      });
      viewAllConversations(); // sidebar preview/order update
    });

    return () => {
      echo.leave(channelName);
    };
  }, [authToken, selectedChat?.id]);

  // viewAllMessages — ab Bearer token bhi bhejta hai (route guard:Kmseller ke andar aa gaya)
  const viewAllMessages = (conversationId) => {
    const formData = new FormData();
    formData.append("kmconversation_id", conversationId);

    axios
      .post(`${baseUrl}/seller/chat/viewAllMessages`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setMessages(response.data.messages || []);
        }
      });
  };

  // sendMessage — Bearer token add kiya
  const sendMessage = () => {
    if (!messageText.trim() && !attachedFile) return;
    if (!selectedChat) return;

    const formData = new FormData();
    formData.append("kmconversation_id", selectedChat.id);
    formData.append("send_by", "seller");
    formData.append("message", messageText);
    formData.append("image", attachedFile);

    axios
      .post(`${baseUrl}/seller/chat/sendMessage`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      })
      .then((response) => {
        if (response.data.hasOwnProperty("error")) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        } else {
          setMessageText("");
          setAttachedFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
          viewAllMessages(selectedChat.id);
          viewAllConversations();
        }
      });
  };

  // Filter
  const filteredChats = conversations.filter((conv) =>
    conv.kmcustomer?.name?.toLowerCase().includes(searchText.toLowerCase()),
  );

  // Format timestamp
  const formatTime = (datetime) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (loading) {
    return (
      <SellerLayout>
        <MessagesPageSkeleton />
      </SellerLayout>
    );
  }

  return (
    <SellerLayout>
      <div className="msg-container">
        <div className="msg-wrapper">
          {/* SIDEBAR — Chat List */}
          <div className="msg-sidebar">
            <div className="msg-sidebar-header">
              <h3>Messages</h3>
            </div>

            {/* Search */}
            <div className="msg-search">
              <FaSearch />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>

            {/* Chat List */}
            <div className="msg-chat-list">
              {filteredChats.map((conv) => (
                <div
                  key={conv.id}
                  className={`msg-chat-item ${selectedChat?.id === conv.id ? "active" : ""}`}
                  onClick={() => {
                    setSelectedChat(conv);
                    viewAllMessages(conv.id);
                  }}
                >
                  <div className="msg-chat-avatar">
                    <div className="msg-avatar-circle">
                      {conv.kmcustomer?.name?.charAt(0)}
                    </div>
                  </div>
                  <div className="msg-chat-info">
                    <div className="msg-chat-header">
                      <h6>{conv.kmcustomer?.name}</h6>
                      <span className="msg-chat-time">
                        {formatTime(conv.updated_at)}
                      </span>
                    </div>
                    <div className="msg-chat-preview">
                      <p>{conv.lastMessage}</p>
                      {conv.status === "unread" && (
                        <span className="msg-unread-badge">new</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {filteredChats.length === 0 && (
                <div className="msg-no-chats">
                  <p>No conversations found</p>
                </div>
              )}
            </div>
          </div>

          {/* CHAT AREA */}
          <div className="msg-chat-area">
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="msg-chat-header">
                  <div className="msg-header-left">
                    <div className="msg-avatar-circle">
                      {selectedChat.kmcustomer?.name?.charAt(0)}
                    </div>
                    <div className="msg-header-info">
                      <h5>{selectedChat.kmcustomer?.name}</h5>
                    </div>
                  </div>
                  <div className="msg-header-actions">
                    <button className="msg-action-btn">
                      <FaEllipsisV />
                    </button>
                  </div>
                </div>

                {/* Messages */}
                <div className="msg-messages-area">
                  <div className="msg-messages-wrapper">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`msg-message ${msg.send_by === "seller" ? "sent" : "received"}`}
                      >
                        <div className="msg-bubble">
                          {msg.image && (
                            <img
                              src={`${baseUrlForFiles}/${msg.image}`}
                              alt="attachment"
                              className="msg-image"
                            />
                          )}
                          {msg.message && <p>{msg.message}</p>}
                          <div className="msg-meta">
                            <span className="msg-time">
                              {formatTime(msg.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* File Preview */}
                {attachedFile && (
                  <div className="msg-file-preview">
                    <span>📎 {attachedFile.name}</span>
                    <button
                      onClick={() => {
                        setAttachedFile(null);
                        if (fileInputRef.current)
                          fileInputRef.current.value = "";
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Message Input */}
                <div className="msg-input-area">
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={(e) => setAttachedFile(e.target.files[0])}
                  />

                  <button
                    className="msg-attach-btn"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <FaPaperclip />
                  </button>

                  <div className="msg-input-wrapper">
                    <input
                      type="text"
                      placeholder="Type a message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                    <button className="msg-emoji-btn">
                      <FaSmile />
                    </button>
                  </div>

                  <button
                    className="msg-send-btn"
                    onClick={() => sendMessage()}
                    disabled={!messageText.trim() && !attachedFile}
                  >
                    <FaPaperPlane />
                  </button>
                </div>
              </>
            ) : (
              <div className="msg-empty-state">
                <div className="msg-empty-icon">💬</div>
                <h3>Select a conversation</h3>
                <p>Choose a customer to start messaging</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </SellerLayout>
  );
};

export default SellerMessages;
